/**
 * Business Portal API client.
 *
 * MOCK_BUSINESS_API toggle:
 *   - true  → use in-memory mock data + localStorage persistence (no backend
 *             dependency). Useful only when the backend endpoints are down.
 *   - false → call real endpoints on REACT_APP_BACKEND_URL (the default).
 *
 * As of 2026-05 the real /api/auth/business/* endpoints are live in production,
 * so this is set to false. Flip back to true only for offline UI work.
 */
export const MOCK_BUSINESS_API = false;

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "buskerbeat_business_token";
const MOCK_USER_KEY = "buskerbeat_business_mock_user";
const MOCK_EVENTS_KEY = "buskerbeat_business_mock_events";

// ---------- Token helpers ----------
export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(t) {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch {}
}
export function clearAuth() {
  setToken(null);
  try {
    localStorage.removeItem(MOCK_USER_KEY);
    localStorage.removeItem(MOCK_EVENTS_KEY);
  } catch {}
}

// ---------- Mock storage helpers ----------
function mockGetUser() {
  try { return JSON.parse(localStorage.getItem(MOCK_USER_KEY) || "null"); } catch { return null; }
}
function mockSetUser(u) {
  try { localStorage.setItem(MOCK_USER_KEY, JSON.stringify(u)); } catch {}
}
function mockGetEvents() {
  try { return JSON.parse(localStorage.getItem(MOCK_EVENTS_KEY) || "[]"); } catch { return []; }
}
function mockSetEvents(arr) {
  try { localStorage.setItem(MOCK_EVENTS_KEY, JSON.stringify(arr)); } catch {}
}
function slugify(s) {
  return String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
}
function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = Math.random()*16|0; const v = c==='x'?r:(r&0x3)|0x8; return v.toString(16);
  });
}
function delay(ms) { return new Promise(r => setTimeout(r, ms)); }

// ---------- Real backend client ----------
async function http(path, opts = {}) {
  const url = `${BACKEND}${path}`;
  const headers = { "Content-Type": "application/json", Accept: "application/json", ...(opts.headers || {}) };
  const tk = getToken();
  if (tk && !headers.Authorization) headers.Authorization = `Bearer ${tk}`;

  let res;
  try {
    res = await fetch(url, { ...opts, headers });
  } catch (netErr) {
    // Network-level failure (DNS, CORS, offline, Cloudflare interstitial, etc.)
    const err = new Error(
      "Could not reach the BuskerBeat server. Please check your internet connection and try again."
    );
    err.status = 0;
    err.cause = netErr;
    throw err;
  }

  // Read body as text first so we can produce a useful error if it isn't JSON.
  const raw = await res.text();
  let body = null;
  if (raw) {
    try { body = JSON.parse(raw); } catch {
      if (!res.ok) {
        const err = new Error(`Request failed (HTTP ${res.status})`);
        err.status = res.status; err.raw = raw.slice(0, 200);
        throw err;
      }
      // 2xx with non-JSON body is unexpected; treat as an error so callers
      // don't silently get `null` and crash later.
      const err = new Error("Unexpected response from server. Please try again.");
      err.status = res.status; err.raw = raw.slice(0, 200);
      throw err;
    }
  }
  if (!res.ok) {
    let msg = `Request failed (HTTP ${res.status})`;
    if (body && body.detail !== undefined) {
      if (typeof body.detail === "string") {
        msg = body.detail;
      } else if (Array.isArray(body.detail)) {
        // FastAPI 422 validation error format:
        // [{ loc: ["body","latitude"], msg: "...", type: "..." }, ...]
        msg = body.detail
          .map((d) => {
            const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
            const m = d?.msg || "Invalid value";
            return field ? `${field}: ${m}` : m;
          })
          .join(" • ");
      } else if (typeof body.detail === "object") {
        msg = body.detail.msg || JSON.stringify(body.detail);
      }
    } else if (body && body.message) {
      msg = body.message;
    }
    const err = new Error(msg);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

// ---------- API surface ----------
export async function registerBusiness(payload) {
  if (MOCK_BUSINESS_API) {
    await delay(450);
    if (!payload.email || !payload.password || !payload.business_name) {
      const e = new Error("Email, password and business name are required."); e.status = 400; throw e;
    }
    if (String(payload.password).length < 6) {
      const e = new Error("Password must be at least 6 characters."); e.status = 400; throw e;
    }
    const user = {
      id: uuid(),
      role: "business",
      username: slugify(payload.business_name) || "venue-" + Math.floor(Math.random()*1000),
      verified: false,
      profile_photo: null,
      cover_photo: null,
      gallery: [],
      hours: {},
      created_at: new Date().toISOString(),
      ...payload,
    };
    delete user.password;
    mockSetUser(user);
    const token = "mock." + uuid();
    setToken(token);
    return { token, user };
  }
  const body = await http("/api/auth/business/register", { method: "POST", body: JSON.stringify(payload) });
  if (body && body.token) setToken(body.token);
  return body;
}

export async function loginBusiness({ email, password }) {
  if (MOCK_BUSINESS_API) {
    await delay(400);
    const stored = mockGetUser();
    if (!stored || stored.email !== email) {
      const e = new Error("No business account found for this email."); e.status = 401; throw e;
    }
    // Mock doesn't verify password strength; in real mode the backend does.
    if (!password || password.length < 6) {
      const e = new Error("Invalid email or password."); e.status = 401; throw e;
    }
    const token = "mock." + uuid();
    setToken(token);
    return { token, user: stored };
  }
  const body = await http("/api/auth/business/login", { method: "POST", body: JSON.stringify({ email, password }) });
  if (body && body.token) setToken(body.token);
  return body;
}

export async function getMe() {
  if (MOCK_BUSINESS_API) {
    await delay(200);
    const u = mockGetUser();
    if (!getToken() || !u) { const e = new Error("Not authenticated"); e.status = 401; throw e; }
    return u;
  }
  return await http("/api/auth/business/me");
}

export async function updateMe(patch) {
  if (MOCK_BUSINESS_API) {
    await delay(350);
    const u = mockGetUser() || {};
    const next = { ...u, ...patch, updated_at: new Date().toISOString() };
    mockSetUser(next);
    return next;
  }
  return await http("/api/businesses/me", { method: "PUT", body: JSON.stringify(patch) });
}

// ---------- Multi-venue / multi-location ----------
// Backend contract (all require Bearer business JWT, snake_case JSON):
//   GET    /api/business/my-locations
//   POST   /api/business/locations
//   PUT    /api/business/locations/{id}
//   PUT    /api/business/locations/{id}/set-primary
//   PATCH  /api/business/locations/{id}/slug   body: { "slug": "..." }
//   DELETE /api/business/locations/{id}        (cannot delete last)
//
// GET /api/auth/business/me now also returns `locations: [...]` on the
// owner object — components can choose to seed from there or call
// listMyLocations() explicitly.
export async function listMyLocations() {
  return await http("/api/business/my-locations");
}
export async function createLocation(payload) {
  return await http("/api/business/locations", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
export async function updateLocation(id, payload) {
  return await http(`/api/business/locations/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}
export async function setPrimaryLocation(id) {
  return await http(`/api/business/locations/${encodeURIComponent(id)}/set-primary`, {
    method: "PUT",
  });
}
export async function updateLocationSlug(id, slug) {
  return await http(`/api/business/locations/${encodeURIComponent(id)}/slug`, {
    method: "PATCH",
    body: JSON.stringify({ slug }),
  });
}
export async function deleteLocation(id) {
  return await http(`/api/business/locations/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// Business type options used by venue create/edit forms.
export const BUSINESS_TYPE_OPTIONS = [
  { value: "cafe", label: "Café" },
  { value: "bar", label: "Bar" },
  { value: "restaurant", label: "Restaurant" },
  { value: "music_venue", label: "Music Venue" },
  { value: "festival", label: "Festival" },
  { value: "retail", label: "Retail" },
  { value: "hotel", label: "Hotel" },
  { value: "other", label: "Other" },
];

export async function createEvent(payload) {
  if (MOCK_BUSINESS_API) {
    await delay(450);
    const events = mockGetEvents();
    const evt = { id: uuid(), status: "open", created_at: new Date().toISOString(), ...payload };
    events.unshift(evt);
    mockSetEvents(events);
    return evt;
  }
  return await http("/api/events", { method: "POST", body: JSON.stringify(payload) });
}

export async function listMyEvents() {
  if (MOCK_BUSINESS_API) {
    await delay(150);
    return mockGetEvents();
  }
  return await http("/api/events?mine=true");
}

export async function getPublicBusiness(username) {
  // PUBLIC endpoint — never sends Authorization header. Used by the
  // /business/:username profile page which must work for anonymous visitors.
  const url = `${BACKEND}/api/businesses/${encodeURIComponent(username)}`;
  let res;
  try { res = await fetch(url, { headers: { Accept: "application/json" } }); }
  catch (e) {
    const err = new Error("Could not reach the BuskerBeat server. Please check your internet connection.");
    err.status = 0; throw err;
  }
  const raw = await res.text();
  let body = null;
  if (raw) { try { body = JSON.parse(raw); } catch {} }
  if (res.status === 404) { const err = new Error("Business not found"); err.status = 404; throw err; }
  if (!res.ok) {
    const msg = (body && (typeof body.detail === "string" ? body.detail : null)) || `Request failed (HTTP ${res.status})`;
    const err = new Error(msg); err.status = res.status; throw err;
  }
  return body;
}

export async function submitVenueInquiry(payload) {
  // POST to /api/events with a venue inquiry. May require auth on the backend;
  // we surface the backend's error verbatim if so.
  return await http("/api/events", { method: "POST", body: JSON.stringify(payload) });
}

// ---------- Hire requests (public gigs the business posts) ----------

// Map country name (free-text) → ISO-4217 currency code. Best-effort
// auto-pick; the user can always change the currency in the form.
const COUNTRY_TO_CURRENCY = {
  "australia": "AUD", "au": "AUD",
  "united states": "USD", "usa": "USD", "us": "USD", "united states of america": "USD",
  "united kingdom": "GBP", "uk": "GBP", "great britain": "GBP", "england": "GBP", "scotland": "GBP", "wales": "GBP",
  "canada": "CAD", "ca": "CAD",
  "new zealand": "NZD", "nz": "NZD",
  "singapore": "SGD", "sg": "SGD",
  "ireland": "EUR", "germany": "EUR", "france": "EUR", "spain": "EUR", "italy": "EUR",
  "netherlands": "EUR", "portugal": "EUR", "austria": "EUR", "belgium": "EUR", "finland": "EUR", "greece": "EUR",
  "japan": "JPY", "jp": "JPY",
  "india": "INR", "in": "INR",
  "switzerland": "CHF",
  "sweden": "SEK",
  "norway": "NOK",
  "denmark": "DKK",
  "south africa": "ZAR",
  "mexico": "MXN",
  "brazil": "BRL",
  "uae": "AED", "united arab emirates": "AED",
  "hong kong": "HKD",
  "philippines": "PHP",
  "indonesia": "IDR",
  "thailand": "THB",
  "malaysia": "MYR",
  "vietnam": "VND",
};
export function currencyForCountry(country) {
  if (!country) return "USD";
  const key = String(country).trim().toLowerCase();
  return COUNTRY_TO_CURRENCY[key] || "USD";
}

export const SUPPORTED_CURRENCIES = [
  "AUD", "USD", "GBP", "EUR", "CAD", "NZD", "SGD", "JPY", "INR", "CHF",
  "SEK", "NOK", "DKK", "ZAR", "MXN", "BRL", "AED", "HKD", "PHP", "IDR", "THB", "MYR", "VND",
];

export const PERFORMER_TYPE_OPTIONS = [
  { value: "singer", label: "Singer" },
  { value: "beatboxer", label: "Beatboxer" },
  { value: "dj", label: "DJ" },
  { value: "jazz", label: "Jazz" },
  { value: "band", label: "Band" },
  { value: "other", label: "Other" },
];

export async function createHireRequest(payload) {
  return await http("/api/hire-requests", { method: "POST", body: JSON.stringify(payload) });
}
export async function listMyHireRequests() {
  return await http("/api/hire-requests/my-requests");
}
export async function getHireRequestApplications(requestId) {
  return await http(`/api/hire-requests/${encodeURIComponent(requestId)}/applications`);
}
export async function acceptApplication(applicationId) {
  return await http(`/api/hire-request-applications/${encodeURIComponent(applicationId)}/accept`, { method: "PUT" });
}
export async function rejectApplication(applicationId) {
  return await http(`/api/hire-request-applications/${encodeURIComponent(applicationId)}/reject`, { method: "PUT" });
}
export async function deleteHireRequest(requestId) {
  return await http(`/api/hire-requests/${encodeURIComponent(requestId)}`, { method: "DELETE" });
}

// ---------- Mock data for deferred / coming-soon panels ----------
export const COMING_SOON_STATS = {
  profile_views: 2458,
  foot_traffic: 1328,
  deal_clicks: 974,
  deal_redemptions: 312,
  average_rating: 4.6,
  rating_count: 128,
};

export const COMING_SOON_CHART = [
  { day: "Mon", views: 1700, traffic: 850, clicks: 320 },
  { day: "Tue", views: 2100, traffic: 1100, clicks: 480 },
  { day: "Wed", views: 1950, traffic: 1050, clicks: 360 },
  { day: "Thu", views: 2100, traffic: 1350, clicks: 510 },
  { day: "Fri", views: 2000, traffic: 1080, clicks: 400 },
  { day: "Sat", views: 2400, traffic: 1500, clicks: 560 },
  { day: "Sun", views: 2200, traffic: 1200, clicks: 410 },
];

export const COMING_SOON_PERFORMANCES = [
  { name: "Daniel Park", type: "Acoustic", genre: "Pop, Rock", when: "7:00 PM – 10:00 PM", live: true },
  { name: "The Midnight Vibes", type: "Band", genre: "Indie, Rock", when: "May 18 · 8:00 PM – 11:00 PM" },
  { name: "Sarah Jane", type: "Acoustic", genre: "Jazz, Soul", when: "May 19 · 7:30 PM – 10:30 PM" },
];

export const COMING_SOON_DEALS = [
  { name: "Happy Hour – 1 Free Drink", status: "Active", clicks: 432, redemptions: 186, rate: "43%" },
  { name: "10% Off During Live Music", status: "Active", clicks: 298, redemptions: 96, rate: "32%" },
  { name: "Ladies Night – Special Cocktails", status: "Scheduled", clicks: null, redemptions: null, rate: null },
];

export const COMING_SOON_REVIEWS = [
  { name: "Alex Tan", rating: 5, when: "2 days ago", text: "Great ambience, live music was amazing!" },
  { name: "Melissa Wong", rating: 4.5, when: "4 days ago", text: "Love the vibe and the drinks!" },
  { name: "Jordan Lee", rating: 4, when: "6 days ago", text: "Awesome place to chill with friends." },
];
